import React, { useEffect } from "react";
import styled from "styled-components";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "_metronic/_helpers";
import { ASSISTANT_SPARK } from "app/popleads/components/Chat/assistantIcons";
import { ai } from "./extensionAssistantTheme";

/**
 * The assistant's door.
 *
 * An orb floating over the panel rather than an icon in the header, because the header row is full
 * at the collapsed 300px width — the icons already overflowed onto the wordmark once — and a
 * floating control costs no row space at all. It is also the only round, floating control on this
 * panel besides the Feed tab's own (bottom-right), so what it opens is not in question.
 *
 * Reusing the header's existing "Certified AI Native" icon instead was tried and reverted: that
 * icon is CAIN's own door, the nightly LinkedIn agent, not the assistant's — repointing it hid CAIN
 * behind the assistant rather than giving the assistant a door of its own.
 *
 * The orb is a solid `ai.accent` fill — the extension's own `l3rnTech[900]`, which the CRM window
 * now takes too — rather than the gradient it used to carry. See `extensionAssistantTheme.js` for
 * why the two shells share one accent.
 *
 * **Bottom-left, not bottom-right.** The Feed tab anchors its own floating action button —
 * `ui-kit/FloatingButtons`, "Add New User" / "Generate New Lead" — to the bottom-right corner via
 * its `StickyFooter`, and that corner is where the app already reaches for a floating circular
 * button. Bottom-left is where this one lives instead.
 *
 * **What it opens is the extension's own drawer**, not an overlay of this component's own: the
 * sliding popup that Deals, Notes and Certified AI Native already open into, carrying the assistant
 * at `POPUP_CONTENTS.ASSISTANT`. So the title, the close button and the width posted to the host
 * page are all the popup's, and the assistant is a sibling of CAIN's panel rather than a tab inside
 * it — two different things, two doors, no shared state.
 *
 * That is also why this file holds no dialog: the open flag lives in `ExtensionHome`, which owns the
 * popup, and this component is told whether it is open so the shortcut below can close it.
 */

const Orb = styled.button`
    position: absolute;
    left: 14px;
    /* Set by ExtensionHome's content wrapper: above whichever footer the open tab pins down. */
    bottom: var(--assistant-orb-bottom, 14px);
    z-index: 3;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background-color: ${ai.accent};
    color: #fff;
    /* rgba(30, 30, 79, …) — l3rnTech[950], so the shadow is the orb's own colour, deeper. */
    box-shadow: 0 6px 18px rgba(30, 30, 79, 0.35);
    cursor: pointer;
    transition: transform 0.16s ease, box-shadow 0.16s ease, background-color 0.16s ease;

    svg {
        width: 20px;
        height: 20px;
    }

    &:hover {
        transform: translateY(-1px);
        background-color: ${ai.accentStrong};
        box-shadow: 0 10px 24px rgba(30, 30, 79, 0.42);
    }

    &:focus {
        outline: none;
    }

    &:focus-visible {
        box-shadow: 0 0 0 3px rgba(43, 43, 117, 0.35);
    }

    /* Stays visible while the drawer is open, unlike a modal's trigger would: the drawer slides out
       beside this panel rather than over it, so the orb is never underneath anything — and it is
       then the way back out as well as the way in. */
    ${({ $open }) =>
        $open
            ? `transform: translateY(-1px); background-color: ${ai.accentStrong}; box-shadow: 0 10px 24px rgba(30, 30, 79, 0.42);`
            : ""}
`;

const ExtensionAssistantLauncher = ({ isOpen, onOpen, onClose }) => {
    /**
     * Ctrl/Cmd+K opens the drawer, Escape closes it.
     *
     * This reaches the assistant only while the extension iframe has focus — a keystroke on the
     * host page goes to the host page, and forwarding it is the browser extension's job, not this
     * app's. So the orb is the discoverable way in and this is the fast one, rather than the only
     * one.
     */
    useEffect(() => {
        const onKeyDown = (event) => {
            if ((event.metaKey || event.ctrlKey) && event.key && event.key.toLowerCase() === "k") {
                event.preventDefault();
                if (isOpen) onClose();
                else onOpen();
                return;
            }

            /* Only while the assistant is the open drawer — Escape belongs to whatever else is open
               otherwise, and the panel has several inline editors that use it to cancel. */
            if (event.key === "Escape" && isOpen) onClose();
        };

        document.addEventListener("keydown", onKeyDown);

        return () => document.removeEventListener("keydown", onKeyDown);
    }, [isOpen, onOpen, onClose]);

    return (
        <Orb
            data-assistant-orb
            type="button"
            $open={isOpen}
            aria-expanded={isOpen}
            aria-label="Ask your assistant"
            title="Ask your assistant — Ctrl/Cmd + K"
            onClick={() => (isOpen ? onClose() : onOpen())}
        >
            <SVG src={toAbsoluteUrl(ASSISTANT_SPARK)} />
        </Orb>
    );
};

export default ExtensionAssistantLauncher;
